import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  Output,
  Type,
  ViewChild,
  ViewContainerRef,
  ViewRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ModalService } from './modal.service';
import { Options } from '../../interfaces/modal';
import { DeteccionCambiosService } from '../../../../infrastructure/services/deteccion-cambios.service';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';

@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild('container', { read: ViewContainerRef, static: true })
  container!: ViewContainerRef;

  @ViewChild('panel', { static: true })
  panel!: ElementRef<HTMLElement>;

  public titulo = '';
  protected options: Options = { size: 'sm:my-8 w-full sm:max-w-lg sm:p-6' };
  @Output() cargado = new EventEmitter<boolean>();

  private uiSubscription?: Subscription;
  private componentToLoadSubscription?: Subscription;
  // Vistas detachadas de modales padre mientras un modal hijo está abierto, con el
  // control que abrió al hijo para devolverle el foco al volver.
  private viewStack: { view: ViewRef; options: Options; activador: HTMLElement | null }[] = [];

  private focusTrap?: FocusTrap;
  private isOpenSubscription?: Subscription;
  // Elemento que tenía el foco al abrir el modal; lo recupera al cerrarse.
  private activadorFoco: HTMLElement | null = null;

  @HostListener('document:keydown', ['$event']) onKeydownHandler(
    event: KeyboardEvent,
  ) {
    if (!this.modalService.isOpenValue || event.defaultPrevented) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.modalService.descartar();
    } else if (event.key === 'Enter' && this.esCampoMultilinea(event.target)) {
      // Enter escribe el salto de línea; Ctrl o Cmd+Enter es el atajo de la acción principal.
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        this.modalService.primaryButtonAction();
      }
    } else if (event.key === 'Enter' && event.shiftKey) {
      return;
    } else if (event.key === 'Enter' && this.esBotonDelModal(event.target)) {
      return;
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.modalService.primaryButtonAction();
    }
  }

  public constructor(
    public modalService: ModalService,
    private cdr: ChangeDetectorRef,
    private detectaUIService: DeteccionCambiosService,
    private focusTrapFactory: FocusTrapFactory,
  ) {}

  public ngOnInit(): void {
    this.modalService.setModalComponentRef(this);
  }

  public ngAfterViewInit() {
    this.focusTrap = this.focusTrapFactory.create(this.panel.nativeElement);
    this.focusTrap.enabled = false;
    this.isOpenSubscription = this.modalService.isOpen$.subscribe({
      next: (abierto) => (abierto ? this.alAbrir() : this.alCerrar()),
    });

    this.componentToLoadSubscription =
      this.modalService.componentToLoad$.subscribe({
        next: (component) => this.loadComponent(component),
      });
  }

  public loadComponent(
    component: Type<any>,
    options?: Options,
  ): ComponentRef<any> {
    const anidado = this.modalService.isOpenValue && this.container.length > 0;
    if (anidado) {
      const activo = document.activeElement;
      const activador = activo instanceof HTMLElement && this.panel.nativeElement.contains(activo) ? activo : null;
      const vistaActual = this.container.detach();
      if (vistaActual) {
        this.viewStack.push({ view: vistaActual, options: this.options, activador });
      }
    } else {
      this.viewStack.forEach((nivel) => nivel.view.destroy());
      this.viewStack = [];
      this.container.clear();
    }
    const componentRef = this.container.createComponent(component);
    this.options = { size: 'sm:my-8 w-full sm:max-w-lg sm:p-6' };
    if (options) {
      this.options = options;
    }
    this.modalService.open();
    if (anidado) {
      // El primer nivel lo enfoca alAbrir; en uno anidado el foco se quedaría en la vista ya retirada.
      void this.focusTrap?.focusInitialElementWhenReady();
    }

    // Cancelar subscripción anterior si existe para evitar memory leaks
    this.uiSubscription?.unsubscribe();

    // Usar take(1) para auto-desuscribirse después de la primera emisión
    this.uiSubscription = this.detectaUIService
      .getActualizaUI$()
      .pipe(take(1))
      .subscribe(() => {
        this.cdr.markForCheck();
        setTimeout(() => {
          this.cargado.emit(true);
        });
      });

    return componentRef;
  }

  public restorePrevious(): boolean {
    const nivelAnterior = this.viewStack.pop();
    if (!nivelAnterior) {
      return false;
    }

    this.container.clear();
    this.container.insert(nivelAnterior.view);
    this.options = nivelAnterior.options;
    nivelAnterior.activador?.focus();
    this.detectaUIService.setActualizaUI();

    return true;
  }

  public ngOnDestroy(): void {
    this.uiSubscription?.unsubscribe();
    this.componentToLoadSubscription?.unsubscribe();
    this.isOpenSubscription?.unsubscribe();
    this.focusTrap?.destroy();
  }

  private alAbrir(): void {
    if (!this.focusTrap || this.focusTrap.enabled) {
      return;
    }
    const activo = document.activeElement;
    this.activadorFoco =
      activo instanceof HTMLElement && !this.panel.nativeElement.contains(activo)
        ? activo
        : null;
    this.focusTrap.enabled = true;
    this.focusTrap.focusInitialElementWhenReady();
  }

  private alCerrar(): void {
    if (!this.focusTrap?.enabled) {
      return;
    }
    this.focusTrap.enabled = false;
    this.activadorFoco?.focus();
    this.activadorFoco = null;
  }

  private esCampoMultilinea(target: EventTarget | null): boolean {
    return target instanceof HTMLTextAreaElement || (target instanceof HTMLElement && target.isContentEditable);
  }

  private esBotonDelModal(target: EventTarget | null): boolean {
    return (
      target instanceof HTMLButtonElement &&
      this.panel.nativeElement.contains(target)
    );
  }
}
